import React, { useEffect, useState } from "react";
import {
  Box,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  LinearProgress,
  TextField,
  InputAdornment,
} from "@mui/material";
import SearchIcon from '@mui/icons-material/Search';
import { get_admin_payments } from "../Api service/APIvariables";
import CustomPagination from "./CustomPagination";

export default function AdminPayments() {
  const [payments, setPayments] = useState([]);
  const [searchTerm, setSearchTerm] = useState("");
  const [isLoading, setIsLoading] = useState(true);

  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(5);

  const handleChangePage = (event, newPage) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const fetchPayments = async () => {
    try {
      const user_id = localStorage.getItem("user_id");
      const token = localStorage.getItem("auth_token");
      if (!user_id || !token) return;

      const formData = new FormData();
      formData.append("user_id", user_id);

      const res = await fetch(get_admin_payments, {
        method: "POST",
        headers: { Authorization: `Bearer ${token}` },
        body: formData,
      });
      const data = await res.json();
      if (!data.error) {
        setPayments(data.data);
      }
    } catch (err) {
      console.error(err);
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    fetchPayments();
    window.addEventListener("planUpdated", fetchPayments);
    return () => window.removeEventListener("planUpdated", fetchPayments);
  }, []);

  const filteredPayments = payments.filter((payment) => {
    const user_name = payment.user_name ? payment.user_name.toLowerCase() : "";
    const user_email = payment.user_email ? payment.user_email.toLowerCase() : "";
    const payment_id = payment.razorpay_payment_id ? payment.razorpay_payment_id.toLowerCase() : "";
    const order_id = payment.razorpay_order_id ? payment.razorpay_order_id.toLowerCase() : "";
    const term = searchTerm.toLowerCase();

    return (
      user_name.includes(term) ||
      user_email.includes(term) ||
      payment_id.includes(term) ||
      order_id.includes(term)
    );
  });

  return (
    <Box sx={{ padding: 4, width: "100%", maxWidth: "1200px", margin: "0 auto" }}>
      <Typography variant="h4" fontWeight="bold" sx={{ mb: 1, color: "#453c72" }}>
        Payment History
      </Typography>
      <Typography variant="subtitle1" color="textSecondary" sx={{ mb: 4 }}>
        Read-only record of all client subscription upgrades.
      </Typography>

      <Box sx={{ mb: 3 }}>
        <TextField
          placeholder="Search by client name, email, payment ID, order ID..."
          variant="outlined"
          fullWidth
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon color="action" />
              </InputAdornment>
            ),
          }}
          sx={{ backgroundColor: "#fff", borderRadius: 2 }}
        />
      </Box>

      {isLoading ? (
        <LinearProgress color="secondary" sx={{ borderRadius: 2 }} />
      ) : (
        <TableContainer component={Paper} elevation={2} sx={{ borderRadius: 2, overflowX: "auto" }}>
          <Table sx={{ minWidth: 650 }}>
            <TableHead sx={{ backgroundColor: "#f5f5f5" }}>
              <TableRow>
                <TableCell><strong>ID</strong></TableCell>
                <TableCell><strong>Client</strong></TableCell>
                <TableCell><strong>Email</strong></TableCell>
                <TableCell><strong>Plan</strong></TableCell>
                <TableCell><strong>Payment ID</strong></TableCell>
                <TableCell><strong>Order ID</strong></TableCell>
                <TableCell align="right"><strong>Amount</strong></TableCell>
                <TableCell><strong>Date</strong></TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {filteredPayments
                .slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage)
                .map((payment) => (
                <TableRow key={payment.id} hover>
                  <TableCell>{payment.id}</TableCell>
                  <TableCell>{payment.user_name || `User ID: ${payment.user_id}`}</TableCell>
                  <TableCell>{payment.user_email || "N/A"}</TableCell>
                  <TableCell>
                    <Box sx={{
                      display: 'inline-block',
                      px: 1.5, py: 0.5,
                      borderRadius: 1,
                      backgroundColor: payment.plan_name === 'Pro' ? '#e1bee7' : 
                                       payment.plan_name === 'Plus' ? '#bbdefb' : 
                                       payment.plan_name === 'Unlimited' ? '#fff9c4' : '#f5f5f5',
                      color: payment.plan_name === 'Pro' ? '#6a1b9a' : 
                             payment.plan_name === 'Plus' ? '#1565c0' : 
                             payment.plan_name === 'Unlimited' ? '#f57f17' : '#616161',
                      fontWeight: 'bold',
                      fontSize: '0.85rem'
                    }}>
                      {payment.plan_name || 'Free'}
                    </Box>
                  </TableCell>
                  <TableCell sx={{ fontFamily: "monospace" }}>{payment.razorpay_payment_id}</TableCell>
                  <TableCell sx={{ fontFamily: "monospace" }}>{payment.razorpay_order_id}</TableCell>
                  <TableCell align="right" sx={{ fontWeight: "bold", color: "#2e7d32" }}>
                    ₹{parseFloat(payment.amount).toLocaleString()}
                  </TableCell>
                  <TableCell>{new Date(payment.created_at).toLocaleString()}</TableCell>
                </TableRow>
              ))}
              {filteredPayments.length === 0 && (
                <TableRow>
                  <TableCell colSpan={8} align="center" sx={{ py: 3, color: "text.secondary" }}>
                    No payment records found.
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </TableContainer>
      )}

      {!isLoading && (
        <div
          style={{
            display: "flex",
            justifyContent: "flex-end",
            alignItems: "center",
            marginTop: "16px",
            marginRight: "auto",
          }}
        >
          <CustomPagination
            count={filteredPayments.length}
            rowsPerPage={rowsPerPage}
            page={page}
            onPageChange={handleChangePage}
            onRowsPerPageChange={handleChangeRowsPerPage}
            sx={{
              fontSize: "12px",
              width: "auto",
              justifyContent: "space-between",
              marginBottom: 2,
            }}
          />
        </div>
      )}
    </Box>
  );
}
